import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { Button } from "./ui/Button";
import {
  Chip,
  Popover,
  Box,
  TextField,
  Switch,
  FormControlLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import type { SchemaField } from "oschema";
import { AutocompleteSelect } from "./AutocompleteSelect";
import {
  Field,
  InputKind,
  enumOptions,
  fieldLabel,
  inputKindFor,
  isNativeDate,
  isNativeNumber,
} from "../lib/schema-field";
import {
  toDateInput,
  toDateTimeInput,
  toTimeInput,
} from "./DynamicFormField";

// Keep drawer chrome in sync with DynamicFormField without importing form styles.
const inputSx = {
  width: "100%",
  "&& .MuiOutlinedInput-root:not(.MuiInputBase-multiline)": {
    backgroundColor: "var(--od-surface)",
    color: "var(--od-text)",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--od-interactive-boundary)" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "var(--od-primary)" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "var(--od-primary)" },
  },
};

export interface FilterCondition {
  id: string;
  field: string;
  operator: string;
  value: string | string[] | boolean;
}

interface FilterChipsProps {
  schema: SchemaField[];
  onFilterChange: (filterGroups: string | undefined) => void;
  initialFilters?: string;
  /** 44px targets. Phone collection search has no desktop toolbar. */
  touch?: boolean;
}

const operators: { value: string; label: string; fullLabel: string }[] = [
  { value: "==", label: "=", fullLabel: "Equals" },
  { value: "!=", label: "≠", fullLabel: "Not Equals" },
  { value: ">", label: ">", fullLabel: "Greater Than" },
  { value: ">=", label: "≥", fullLabel: "Greater or Equal" },
  { value: "<", label: "<", fullLabel: "Less Than" },
  { value: "<=", label: "≤", fullLabel: "Less or Equal" },
  { value: "contains", label: "contains", fullLabel: "Contains" },
  { value: "startsWith", label: "starts with", fullLabel: "Starts With" },
  { value: "endsWith", label: "ends with", fullLabel: "Ends With" },
  { value: "in", label: "in", fullLabel: "In List" },
  { value: "notIn", label: "not in", fullLabel: "Not In List" },
  { value: "isNull", label: "∅", fullLabel: "Is empty" },
  { value: "isNotNull", label: "≠∅", fullLabel: "Is not empty" },
];

const OPS_EQ = ["==", "!="];
const OPS_RANGE = ["==", "!=", ">", ">=", "<", "<="];
const OPS_NUMERIC = ["==", "!=", ">", ">=", "<", "<=", "in", "notIn"];
const OPS_ENUM = ["==", "!=", "in", "notIn"];
const OPS_TEXT = ["==", "!=", "contains", "startsWith", "endsWith", "in", "notIn"];
const OPS_JSON = ["contains", "==", "!="];
const OPS_NULL = ["isNull", "isNotNull"];

const isNullnessOp = (op: string) => op === "isNull" || op === "isNotNull";
const isListOp = (op: string) => op === "in" || op === "notIn";

const emptyValueForKind = (kind: InputKind): string | boolean =>
  kind === "boolean" ? false : "";

const hasFilterValue = (f: {
  operator: string;
  value: FilterCondition["value"];
}): boolean => {
  if (!f.operator) return false;
  if (isNullnessOp(f.operator)) return true;
  if (f.value === undefined || f.value === null) return false;
  if (Array.isArray(f.value)) return f.value.length > 0;
  if (typeof f.value === "boolean") return true;
  return String(f.value).trim() !== "";
};

const asScalar = (value: FilterCondition["value"]): string | boolean => {
  if (Array.isArray(value)) return value[0] ?? "";
  return value;
};

const asList = (value: FilterCondition["value"]): string[] => {
  if (Array.isArray(value)) {
    return value.map(String).map((v) => v.trim()).filter(Boolean);
  }
  if (typeof value === "boolean") return [value ? "true" : "false"];
  const s = String(value ?? "").trim();
  if (!s) return [];
  if (s.includes(",")) {
    return s.split(",").map((part) => part.trim()).filter(Boolean);
  }
  return [s];
};

const valueForOperator = (
  op: string,
  kind: InputKind,
  value: FilterCondition["value"],
): FilterCondition["value"] => {
  if (isNullnessOp(op)) return "";
  if (isListOp(op)) return asList(value);
  if (kind === "boolean") {
    const s = asScalar(value);
    if (typeof s === "boolean") return s;
    const t = String(s).trim().toLowerCase();
    return t === "true" || t === "yes" || t === "1";
  }
  const s = asScalar(value);
  return typeof s === "boolean" ? (s ? "true" : "false") : String(s ?? "");
};

const emptyValueForOperator = (op: string, kind: InputKind) =>
  valueForOperator(op, kind, isListOp(op) ? [] : emptyValueForKind(kind));

/** Operators follow `inputKindFor`, not the analyser's raw `type`. */
const operatorValuesForKind = (kind: InputKind): string[] => {
  switch (kind) {
    case "boolean":
      return OPS_EQ;
    case "enum":
      return OPS_ENUM;
    case "number":
    case "currency":
    case "percentage":
      return OPS_NUMERIC;
    case "date":
    case "datetime":
    case "time":
      return OPS_RANGE;
    case "json":
      return OPS_JSON;
    default:
      return OPS_TEXT;
  }
};

const asField = (field: SchemaField): Field => field as Field;

const getOperatorsForField = (field: SchemaField | undefined) => {
  if (!field) return operators;
  const allowed = new Set(operatorValuesForKind(inputKindFor(asField(field))));
  if (asField(field).nullable !== false) {
    OPS_NULL.forEach((op) => allowed.add(op));
  }
  return operators.filter((op) => allowed.has(op.value));
};

const getOperatorLabel = (op: string) =>
  operators.find((o) => o.value === op)?.label || op;

const formatValue = (value: string | string[] | boolean): string => {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value))
    return value.length > 2
      ? `${value.slice(0, 2).join(", ")}...`
      : value.join(", ");
  if (typeof value === "string" && value.length > 20)
    return value.slice(0, 20) + "...";
  return String(value);
};

interface ParsedFilter {
  field: string;
  operator: string;
  value: string | string[] | boolean;
}

const parseInitialFilters = (
  initialFilters?: string
): { filters: FilterCondition[]; operator: "AND" | "OR" } => {
  if (!initialFilters) return { filters: [], operator: "AND" };
  try {
    const parsed = JSON.parse(initialFilters) as {
      filters?: ParsedFilter[];
      operator?: "AND" | "OR";
    };
    const filters: FilterCondition[] = (parsed.filters || []).map(
      (f: ParsedFilter, index: number) => ({
        id: `filter-${Date.now()}-${index}`,
        field: f.field,
        operator: f.operator,
        value: f.value,
      })
    );
    return { filters, operator: parsed.operator || "AND" };
  } catch {
    return { filters: [], operator: "AND" };
  }
};

const toBackendJson = (
  filters: FilterCondition[],
  operator: "AND" | "OR"
): string | undefined => {
  const validFilters = filters.filter((f) => f.field && hasFilterValue(f));
  if (validFilters.length === 0) return undefined;
  return JSON.stringify({
    operator,
    filters: validFilters.map((f) =>
      isNullnessOp(f.operator)
        ? { field: f.field, operator: f.operator }
        : { field: f.field, operator: f.operator, value: f.value },
    ),
  });
};

const enumEqualsSelect = (opts: {
  enumVals: string[];
  value: string | string[] | boolean;
  onChange: (value: string) => void;
}): React.ReactNode => {
  const { enumVals, value, onChange } = opts;
  return (
    <AutocompleteSelect
      label="Value"
      options={enumVals.map((v) => ({ label: v, value: v }))}
      value={Array.isArray(value) ? "" : String(value ?? "")}
      onChange={(v) => onChange(typeof v === "string" ? v : "")}
    />
  );
};

const filterValueInputForKind = (opts: {
  kind: InputKind;
  field?: Field;
  value: string | string[] | boolean;
  onChange: (value: string | boolean) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onKeyDown: (e: React.KeyboardEvent) => void;
}): React.ReactNode => {
  const { kind, field, value, onChange, inputRef, onKeyDown } = opts;
  const enumVals = field ? enumOptions(field) : undefined;

  if (kind === "boolean") {
    return (
      <FormControlLabel
        control={
          <Switch
            checked={Boolean(value)}
            onChange={(_, checked) => onChange(checked)}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: "var(--od-primary)",
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "var(--od-primary)",
              },
            }}
          />
        }
        label={Boolean(value) ? "Yes" : "No"}
      />
    );
  }

  if (kind === "enum" && enumVals && enumVals.length > 0) {
    return enumEqualsSelect({
      enumVals,
      value,
      onChange,
    });
  }

  if (kind === "date" || kind === "datetime" || kind === "time") {
    const convert =
      kind === "date"
        ? toDateInput
        : kind === "datetime"
          ? toDateTimeInput
          : toTimeInput;
    const raw = Array.isArray(value) ? "" : value == null ? "" : String(value);
    const converted = convert(raw);
    const untrusted = kind !== "time" && !(field && isNativeDate(field));
    const unparseable = raw !== "" && converted === "";
    const native = !untrusted && !unparseable;
    const inputType = native
      ? kind === "date"
        ? "date"
        : kind === "datetime"
          ? "datetime-local"
          : "time"
      : "text";
    const display = native ? converted || raw : raw;
    return (
      <TextField
        sx={inputSx}
        inputRef={inputRef}
        size="small"
        type={inputType}
        label="Value"
        value={display}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        fullWidth
        InputLabelProps={{ shrink: true }}
      />
    );
  }

  if (kind === "number" || kind === "currency" || kind === "percentage") {
    const native = !!(field && isNativeNumber(field));
    return (
      <TextField
        sx={inputSx}
        inputRef={inputRef}
        size="small"
        type={native ? "number" : "text"}
        inputMode="decimal"
        label="Value"
        value={String(value ?? "")}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        fullWidth
        InputProps={
          kind === "currency"
            ? {
                startAdornment: (
                  <span className="mr-1 text-xs text-od-muted">$</span>
                ),
              }
            : kind === "percentage"
              ? {
                  endAdornment: (
                    <span className="ml-1 text-xs text-od-muted">%</span>
                  ),
                }
              : undefined
        }
      />
    );
  }

  if (kind === "color") {
    const hex = String(value || "");
    return (
      <div className="flex items-center gap-2">
        <input
          type="color"
          aria-label="Colour"
          className="h-11 w-11 shrink-0 cursor-pointer rounded border border-od-interactive-boundary bg-od-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-od-text sm:h-9 sm:w-9 [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11"
          value={/^#[0-9a-fA-F]{6}$/.test(hex) ? hex : "#000000"}
          onChange={(e) => onChange(e.target.value)}
        />
        <TextField
          sx={inputSx}
          inputRef={inputRef}
          size="small"
          label="Value"
          value={hex}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          fullWidth
          placeholder="#rrggbb"
        />
      </div>
    );
  }

  return (
    <TextField
      sx={inputSx}
      inputRef={inputRef}
      size="small"
      type="text"
      inputMode={
        kind === "email" ? "email" : kind === "phone" ? "tel" : "text"
      }
      label="Value"
      value={String(value || "")}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      fullWidth
      placeholder="Enter value..."
    />
  );
};

interface FilterPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  schema: SchemaField[];
  filter: FilterCondition | null;
  onSave: (filter: FilterCondition) => void;
  onClose: () => void;
  fieldOptions: { label: string; value: string }[];
}

const FilterPopover: React.FC<FilterPopoverProps> = ({
  open,
  anchorEl,
  schema,
  filter,
  onSave,
  onClose,
  fieldOptions,
}) => {
  const [localFilter, setLocalFilter] = useState<FilterCondition>(
    () =>
      filter || {
        id: `filter-${Date.now()}`,
        field: schema[0]?.name || "",
        operator: "==",
        value: "",
      }
  );
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    // Reset only when the popover opens or the edited chip changes. `schema`
    // is often a new array every parent render; depending on it re-ran this
    // effect and the old 100ms focus() closed whatever list the user had open.
    setLocalFilter(
      filter || {
        id: `filter-${Date.now()}`,
        field: schema[0]?.name || "",
        operator: "==",
        value: emptyValueForKind(
          schema[0] ? inputKindFor(asField(schema[0])) : "text"
        ),
      }
    );
    // schema is read at open time only — see comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, filter]);

  const selectedField = schema.find((f) => f.name === localFilter.field);
  const selectedKind = selectedField
    ? inputKindFor(asField(selectedField))
    : "text";
  const availableOperators = getOperatorsForField(selectedField);

  const handleFieldChange = (fieldName: string) => {
    const newField = schema.find((f) => f.name === fieldName);
    const newOperators = getOperatorsForField(newField);
    const currentOpValid = newOperators.some(
      (op) => op.value === localFilter.operator
    );
    const kind = newField ? inputKindFor(asField(newField)) : "text";
    const nextOp = currentOpValid
      ? localFilter.operator
      : newOperators[0]?.value || "==";
    setLocalFilter({
      ...localFilter,
      field: fieldName,
      operator: nextOp,
      value: emptyValueForOperator(nextOp, kind),
    });
  };

  const handleOperatorChange = (operator: string) => {
    setLocalFilter({
      ...localFilter,
      operator,
      value: valueForOperator(operator, selectedKind, localFilter.value),
    });
  };

  const handleSave = () => {
    if (!localFilter.field || !hasFilterValue(localFilter)) return;
    onSave(
      isNullnessOp(localFilter.operator)
        ? { ...localFilter, value: "" }
        : localFilter,
    );
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  const renderValueInput = () => {
    const op = localFilter.operator;
    const field = selectedField ? asField(selectedField) : undefined;
    const enumVals = field ? enumOptions(field) : undefined;

    if (isNullnessOp(op)) {
      return null;
    }

    if (isListOp(op)) {
      const options = (enumVals ?? []).map((v) => ({ label: v, value: v }));
      return (
        <AutocompleteSelect
          label="Values"
          options={options}
          value={Array.isArray(localFilter.value) ? localFilter.value : []}
          onChange={(v) =>
            setLocalFilter({
              ...localFilter,
              value: Array.isArray(v) ? v : [],
            })
          }
          multiple
          freeSolo={!enumVals}
          placeholder={
            enumVals ? "Pick one or more values" : "Type and press Enter"
          }
        />
      );
    }

    return filterValueInputForKind({
      kind: selectedKind,
      field,
      value: localFilter.value,
      onChange: (value) => setLocalFilter({ ...localFilter, value }),
      inputRef,
      onKeyDown: handleKeyDown,
    });
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          sx: {
            p: 2,
            // 320px min used to clip the paper on a 390 phone once the anchor
            // sat near the right edge. Cap to the viewport instead.
            width: "min(400px, calc(100vw - 32px))",
            maxWidth: "min(400px, calc(100vw - 32px))",
            overflow: "visible",
            backgroundColor: "var(--od-surface)",
            border: "1px solid",
            borderColor: "var(--od-border)",
            borderRadius: 2,
          },
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <p className="text-od-muted text-sm">
          {filter ? "Edit Filter" : "Add Filter"}
        </p>

        <AutocompleteSelect
          label="Field"
          options={fieldOptions}
          value={localFilter.field}
          onChange={(v) => handleFieldChange((v as string) || "")}
        />

        <AutocompleteSelect
          label="Operator"
          disableClearable
          options={availableOperators.map((op) => ({
            label: op.fullLabel,
            value: op.value,
          }))}
          value={localFilter.operator}
          onChange={(v) => {
            // Not clearable; guard anyway so an empty pick cannot leave the
            // filter with no operator.
            if (typeof v === "string" && v) handleOperatorChange(v);
          }}
        />

        {renderValueInput()}

        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 1 }}>
          <Button
            onClick={onClose}
            variant="secondary" size="sm"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!localFilter.field || !hasFilterValue(localFilter)}
            variant="primary" size="sm"
          >
            {filter ? "Update" : "Add"}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};

export const FilterChips: React.FC<FilterChipsProps> = ({
  schema,
  onFilterChange,
  initialFilters,
  touch = false,
}) => {
  const [filters, setFilters] = useState<FilterCondition[]>(
    () => parseInitialFilters(initialFilters).filters
  );
  const [groupOperator, setGroupOperator] = useState<"AND" | "OR">(
    () => parseInitialFilters(initialFilters).operator
  );
  const [editingFilter, setEditingFilter] = useState<FilterCondition | null>(
    null
  );
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const isInitialMount = useRef(true);

  const fieldOptions = useMemo(
    () =>
      schema.map((f) => ({
        label: fieldLabel(asField(f)),
        value: f.name,
      })),
    [schema]
  );

  const getFieldLabel = useCallback(
    (fieldName: string) =>
      fieldOptions.find((f) => f.value === fieldName)?.label || fieldName,
    [fieldOptions]
  );

  /**
   * The callback is held in a ref rather than listed as a dependency.
   * `useSearchParams`'s setter is a new function on every URL change, so
   * depending on the callback re-ran this effect whenever *any* query param
   * moved and pushed the local filter state back over whatever had just been
   * written — including a filter added from the grid's right-click menu, which
   * this effect then erased a tick after it landed.
   */
  const onFilterChangeRef = useRef(onFilterChange);
  useEffect(() => {
    onFilterChangeRef.current = onFilterChange;
  }, [onFilterChange]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    onFilterChangeRef.current(toBackendJson(filters, groupOperator));
  }, [filters, groupOperator]);

  /**
   * Filters can also arrive from outside: the grid's right-click menu writes
   * straight to the URL. Without this the query re-ran but the chips still
   * showed the old set, so the toolbar lied about what was applied.
   */
  useEffect(() => {
    const incoming = parseInitialFilters(initialFilters);
    const same =
      incoming.operator === groupOperator &&
      JSON.stringify(
        incoming.filters.map((f) => [f.field, f.operator, f.value])
      ) ===
        JSON.stringify(filters.map((f) => [f.field, f.operator, f.value]));
    if (same) return;
    setFilters(incoming.filters);
    setGroupOperator(incoming.operator);
    // Only the incoming value drives this; comparing against local state here
    // would re-run it on every local edit and fight the effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilters]);

  const handleAddClick = (event: React.MouseEvent<HTMLElement>) => {
    setEditingFilter(null);
    setPopoverAnchor(event.currentTarget);
  };

  const handleChipClick = (
    next: FilterCondition,
    event: React.MouseEvent<HTMLElement>
  ) => {
    setEditingFilter(next);
    setPopoverAnchor(event.currentTarget);
  };

  const handleRemoveFilter = (filterId: string) => {
    setFilters((prev) => prev.filter((f) => f.id !== filterId));
  };

  const handleSaveFilter = (next: FilterCondition) => {
    setFilters((prev) => {
      const existing = prev.find((f) => f.id === next.id);
      if (existing) {
        return prev.map((f) => (f.id === next.id ? next : f));
      }
      return [...prev, next];
    });
  };

  const handleClosePopover = () => {
    setPopoverAnchor(null);
    setEditingFilter(null);
  };

  const handleClearAll = () => {
    setFilters([]);
  };

  const toggleOperator = () => {
    setGroupOperator((prev) => (prev === "AND" ? "OR" : "AND"));
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1,
      }}
    >
      {filters.length > 0 && (
        <FilterListIcon sx={{ color: "var(--od-muted)", fontSize: 18 }} />
      )}

      {filters.length > 0 && (
        <>
          {filters.map((item, index) => (
            <React.Fragment key={item.id}>
              {index > 0 && (
                <Chip
                  label={groupOperator}
                  size="small"
                  onClick={toggleOperator}
                  sx={{
                    height: touch ? 44 : 24,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    cursor: "pointer",
                    backgroundColor:
                      groupOperator === "AND"
                        ? "var(--od-primary)"
                        : "var(--od-accent)",
                    color: groupOperator === "AND"
                      ? "var(--od-primary-contrast)"
                      : "var(--od-accent-contrast)",
                    "&&:hover, &&.Mui-focusVisible, &&:focus-visible": {
                      backgroundColor: groupOperator === "AND"
                        ? "var(--od-primary-hover)"
                        : "var(--od-accent)",
                      color: groupOperator === "AND"
                        ? "var(--od-primary-contrast)"
                        : "var(--od-accent-contrast)",
                      textDecoration: "underline",
                    },
                    "&&.Mui-focusVisible, &&:focus-visible": {
                      outline: "2px solid var(--od-text)",
                      outlineOffset: "2px",
                    },
                    "&&:active": {
                      backgroundColor: groupOperator === "AND"
                        ? "var(--od-primary-active)"
                        : "var(--od-accent)",
                      color: groupOperator === "AND"
                        ? "var(--od-primary-contrast)"
                        : "var(--od-accent-contrast)",
                      textDecoration: "underline",
                      textDecorationThickness: "2px",
                    },
                  }}
                />
              )}
              <Chip
                label={`${getFieldLabel(item.field)} ${getOperatorLabel(
                  item.operator,
                )}${
                  isNullnessOp(item.operator)
                    ? ""
                    : ` ${formatValue(item.value)}`
                }`}
                onClick={(e) => handleChipClick(item, e)}
                onDelete={() => handleRemoveFilter(item.id)}
                sx={{
                  height: touch ? 44 : undefined,
                  minWidth: 0,
                  maxWidth: "100%",
                  "@media (max-width: 639px), (pointer: coarse)": { height: 44 },
                  backgroundColor:
                    "color-mix(in srgb, var(--od-primary) 12%, transparent)",
                  borderColor: "var(--od-primary)",
                  border: "1px solid",
                  color: "var(--od-text)",
                  cursor: "pointer",
                  "& .MuiChip-label": { minWidth: 0 },
                  "&& .MuiChip-deleteIcon": {
                    color: "var(--od-muted)",
                    boxSizing: "border-box",
                    flexShrink: 0,
                    width: touch ? 44 : undefined,
                    height: touch ? 44 : undefined,
                    padding: touch ? "10px" : undefined,
                    margin: touch ? "-1px 0" : undefined,
                    "@media (max-width: 639px), (pointer: coarse)": {
                      width: 44,
                      height: 44,
                      padding: "10px",
                      margin: "-1px 0",
                    },
                    "&:hover, &:active": { color: "var(--od-danger)" },
                  },
                  "&&.Mui-focusVisible .MuiChip-deleteIcon, &&:focus-visible .MuiChip-deleteIcon": {
                    color: "var(--od-danger)",
                  },
                  "&:hover": {
                    backgroundColor:
                      "color-mix(in srgb, var(--od-primary) 20%, transparent)",
                  },
                }}
              />
            </React.Fragment>
          ))}
        </>
      )}

      <Button
        onClick={handleAddClick}
        variant="secondary"
        size={touch ? "md" : "sm"}
        className={touch ? "px-3 text-sm" : undefined}
      >
        <AddIcon sx={{ fontSize: 15 }} />
        <span>Add filter</span>
      </Button>

      {filters.length > 0 && (
        <Button
          onClick={handleClearAll}
          variant="ghost"
          size={touch ? "md" : "sm"}
          className={`ml-auto px-2 hover:bg-transparent hover:text-od-danger ${
            touch ? "text-sm" : ""
          }`}
        >
          Clear all
        </Button>
      )}

      <FilterPopover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        schema={schema}
        filter={editingFilter}
        onSave={handleSaveFilter}
        onClose={handleClosePopover}
        fieldOptions={fieldOptions}
      />
    </Box>
  );
};

export default FilterChips;
